import React, { useEffect, useMemo, useState } from 'react';
import { Heart, MessageCircle, Send, Bookmark, MoreHorizontal, Music2, Film, Layers, Clock } from 'lucide-react';
import { contentCalendarAPI, briefingFileUrl } from '../lib/api';

const _slug = (nombre) => (nombre || 'tu_marca').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9._]/g, '') || 'tu_marca';
export const igHandle = (profileOrName) => (typeof profileOrName === 'object' && profileOrName?.instagram_user) || _slug(typeof profileOrName === 'object' ? profileOrName?.nombre : profileOrName);
const isVideo = (m) => (m?.kind || (m?.content_type || '').split('/')[0]) === 'video';
const fmtIgDate = (ymd) => (ymd ? new Date(`${ymd.slice(0, 10)}T12:00:00`).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' }) : '');
export const defaultPreviewMode = (tipo) => {
  const t = (tipo || '').toLowerCase();
  if (t.startsWith('reel') || t === 'video') return 'reel';
  if (t.startsWith('stor')) return 'story';
  return 'feed';
};

function Avatar({ profile, size = 32, ring = false }) {
  const initial = (profile?.nombre || 'M').trim().charAt(0).toUpperCase();
  const inner = profile?.logo_file_id
    ? <img src={briefingFileUrl(profile.logo_file_id)} alt="" className="w-full h-full object-cover rounded-full bg-white" />
    : <span className="w-full h-full rounded-full bg-gradient-to-br from-amber-400 to-pink-600 text-white font-bold flex items-center justify-center" style={{ fontSize: size * 0.42 }}>{initial}</span>;
  return (
    <span className={`inline-flex shrink-0 rounded-full ${ring ? 'p-[2px] bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600' : ''}`} style={{ width: size, height: size }}>
      <span className={`w-full h-full rounded-full ${ring ? 'p-[2px] bg-white' : ''}`}>{inner}</span>
    </span>
  );
}

function Media({ item, className }) {
  if (!item) return <div className={`${className} bg-neutral-200 flex items-center justify-center text-neutral-400 text-xs`}>Sin imagen</div>;
  return isVideo(item)
    ? <video src={item.url} className={`${className} object-cover`} autoPlay muted loop playsInline />
    : <img src={item.url} alt="" className={`${className} object-cover`} />;
}

function Caption({ handle, copy, lines = 2, light = false }) {
  const [open, setOpen] = useState(false);
  if (!copy) return null;
  return (
    <p className={`text-[13px] leading-[18px] whitespace-pre-wrap ${light ? 'text-white' : 'text-neutral-900'} ${open ? '' : lines === 1 ? 'line-clamp-1' : 'line-clamp-2'}`} data-testid="ig-caption">
      <span className="font-semibold mr-1">{handle}</span>{copy}
      {!open && copy.length > 60 && <button onClick={() => setOpen(true)} className={`ml-1 ${light ? 'text-white/70' : 'text-neutral-500'}`} data-testid="ig-caption-more">… más</button>}
    </p>
  );
}

function FeedFrame({ post, media, profile }) {
  const handle = igHandle(profile);
  const [i, setI] = useState(0);
  const images = media.length ? media : [null];
  return (
    <div className="bg-white text-neutral-900 rounded-xl border overflow-hidden w-full max-w-[380px] mx-auto shadow-sm" data-testid="ig-preview-feed">
      <div className="flex items-center gap-2 px-3 py-2">
        <Avatar profile={profile} size={32} ring />
        <span className="text-[13px] font-semibold flex-1 truncate">{handle}</span>
        <MoreHorizontal className="w-5 h-5" />
      </div>
      <div className="relative">
        <Media item={images[Math.min(i, images.length - 1)]} className="w-full aspect-[4/5]" />
        {images.length > 1 && (
          <>
            <span className="absolute top-2 right-2 text-[11px] bg-black/60 text-white rounded-full px-2 py-0.5">{i + 1}/{images.length}</span>
            {i > 0 && <button onClick={() => setI(i - 1)} className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/80 text-sm" data-testid="ig-carousel-prev">‹</button>}
            {i < images.length - 1 && <button onClick={() => setI(i + 1)} className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/80 text-sm" data-testid="ig-carousel-next">›</button>}
          </>
        )}
      </div>
      <div className="px-3 pt-2.5 pb-3 space-y-1.5">
        <div className="flex items-center gap-4">
          <Heart className="w-6 h-6" /><MessageCircle className="w-6 h-6 -scale-x-100" /><Send className="w-6 h-6" />
          {images.length > 1 && <span className="flex-1 flex justify-center gap-1">{images.map((_, k) => <span key={k} className={`w-1.5 h-1.5 rounded-full ${k === i ? 'bg-sky-500' : 'bg-neutral-300'}`} />)}</span>}
          <Bookmark className="w-6 h-6 ml-auto" />
        </div>
        <Caption handle={handle} copy={post.copy} />
        <p className="text-[10px] uppercase text-neutral-400 tracking-wide">{fmtIgDate(post.fecha)}</p>
      </div>
    </div>
  );
}

function ReelFrame({ post, media, profile }) {
  const handle = igHandle(profile);
  const item = media.find(isVideo) || media[0];
  return (
    <div className="relative bg-black rounded-xl overflow-hidden w-full max-w-[300px] mx-auto aspect-[9/16] shadow-sm" data-testid="ig-preview-reel">
      <Media item={item} className="absolute inset-0 w-full h-full" />
      <div className="absolute inset-x-0 top-0 p-3 flex justify-between text-white text-base font-semibold drop-shadow"><span>Reels</span></div>
      <div className="absolute right-2 bottom-20 flex flex-col items-center gap-4 text-white drop-shadow">
        <Heart className="w-6 h-6" /><MessageCircle className="w-6 h-6 -scale-x-100" /><Send className="w-6 h-6" /><MoreHorizontal className="w-6 h-6" />
      </div>
      <div className="absolute inset-x-0 bottom-0 p-3 pr-12 bg-gradient-to-t from-black/70 to-transparent space-y-1.5">
        <div className="flex items-center gap-2 text-white">
          <Avatar profile={profile} size={28} />
          <span className="text-[13px] font-semibold truncate">{handle}</span>
          <span className="text-[11px] border border-white/80 rounded-md px-1.5 py-0.5">Seguir</span>
        </div>
        <Caption handle="" copy={post.copy} lines={1} light />
        <p className="text-[11px] text-white/90 flex items-center gap-1"><Music2 className="w-3 h-3" /> Audio original · {handle}</p>
      </div>
    </div>
  );
}

function StoryFrame({ media, profile }) {
  const handle = igHandle(profile);
  return (
    <div className="relative bg-black rounded-xl overflow-hidden w-full max-w-[300px] mx-auto aspect-[9/16] shadow-sm" data-testid="ig-preview-story">
      <Media item={media[0]} className="absolute inset-0 w-full h-full" />
      <div className="absolute inset-x-0 top-0 p-2 bg-gradient-to-b from-black/50 to-transparent">
        <div className="flex gap-1 mb-2">{(media.length ? media : [null]).map((_, k) => <span key={k} className={`h-0.5 flex-1 rounded ${k === 0 ? 'bg-white' : 'bg-white/40'}`} />)}</div>
        <div className="flex items-center gap-2 text-white">
          <Avatar profile={profile} size={28} />
          <span className="text-[13px] font-semibold">{handle}</span>
          <span className="text-[12px] text-white/70">2 h</span>
          <MoreHorizontal className="w-5 h-5 ml-auto" />
        </div>
      </div>
      <div className="absolute inset-x-0 bottom-0 p-3 flex items-center gap-3">
        <span className="flex-1 rounded-full border border-white/70 text-white/80 text-[13px] px-3 py-2">Enviar mensaje</span>
        <Heart className="w-6 h-6 text-white" /><Send className="w-6 h-6 text-white" />
      </div>
    </div>
  );
}

const MODES = [{ id: 'feed', label: 'Feed' }, { id: 'reel', label: 'Reel' }, { id: 'story', label: 'Story' }];

export function InstagramPreview({ post, media, profile }) {
  const [mode, setMode] = useState(defaultPreviewMode(post.tipo));
  useEffect(() => { setMode(defaultPreviewMode(post.tipo)); }, [post.id, post.tipo]);
  const list = media || [];
  return (
    <div className="space-y-3" data-testid="ig-preview">
      <div className="flex items-center justify-center gap-1 text-xs">
        <span className="text-muted-foreground mr-1">Vista previa:</span>
        {MODES.map((m) => (
          <button key={m.id} onClick={() => setMode(m.id)}
            className={`px-3 py-1 rounded-full border font-medium transition-colors ${mode === m.id ? 'bg-neutral-900 text-white border-neutral-900' : 'bg-white hover:bg-neutral-100'}`}
            data-testid={`ig-mode-${m.id}`}>{m.label}</button>
        ))}
      </div>
      {mode === 'feed' && <FeedFrame post={post} media={list} profile={profile} />}
      {mode === 'reel' && <ReelFrame post={post} media={list} profile={profile} />}
      {mode === 'story' && <StoryFrame media={list} profile={profile} />}
      <p className="text-[11px] text-center text-muted-foreground">Simulación orientativa: Instagram puede variar ligeramente el recorte.</p>
    </div>
  );
}

// Cuadrícula "Mi feed": próximas publicaciones (aprobadas y pendientes) + publicadas recientes
const FEED_STATES = ['pendiente_aprobar', 'a_programar', 'programado', 'publicado'];
export function FeedGrid({ posts, profile, onOpenPending }) {
  const handle = igHandle(profile);
  const items = useMemo(() => posts
    .filter((p) => FEED_STATES.includes(p.estado) && defaultPreviewMode(p.tipo) !== 'story')
    .sort((a, b) => (b.fecha || '').localeCompare(a.fecha || ''))
    .slice(0, 30), [posts]);
  const pend = items.filter((p) => p.estado === 'pendiente_aprobar').length;
  return (
    <div className="bg-white rounded-xl border max-w-[480px] mx-auto overflow-hidden" data-testid="ig-feed-grid">
      <div className="p-4 flex items-center gap-4">
        <Avatar profile={profile} size={64} ring />
        <div className="min-w-0">
          <p className="font-semibold text-neutral-900 truncate">{handle}</p>
          <p className="text-xs text-neutral-500">{items.length} publicaciones en la cuadrícula{pend ? ` · ${pend} pendientes de tu aprobación` : ''}</p>
        </div>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-center text-neutral-500 py-10" data-testid="ig-feed-empty">Aún no hay publicaciones para mostrar.</p>
      ) : (
        <div className="grid grid-cols-3 gap-0.5">
          {items.map((p) => {
            const reel = defaultPreviewMode(p.tipo) === 'reel';
            const fid = reel ? (p.portada_file_id || p.color_portada_file_id) : (p.color_portada_file_id || p.portada_file_id);
            const pending = p.estado === 'pendiente_aprobar';
            return (
              <button key={p.id} onClick={() => pending && onOpenPending?.(p)}
                className={`relative aspect-[3/4] bg-neutral-200 overflow-hidden ${pending ? 'cursor-pointer' : 'cursor-default'}`}
                title={`${p.titulo || 'Sin título'} · ${fmtIgDate(p.fecha)}`} data-testid={`ig-feed-item-${p.id}`}>
                {fid ? <img src={contentCalendarAPI.getAttachmentUrl(p.id, fid)} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  : <span className="absolute inset-0 flex items-center justify-center text-[10px] text-neutral-500 p-1 text-center">{p.titulo}</span>}
                <span className="absolute top-1 right-1 text-white drop-shadow">{reel ? <Film className="w-4 h-4" /> : (p.tipo || '').toLowerCase().startsWith('carr') ? <Layers className="w-4 h-4" /> : null}</span>
                {pending && <span className="absolute inset-x-0 bottom-0 bg-amber-500/90 text-white text-[10px] font-semibold py-0.5 flex items-center justify-center gap-1"><Clock className="w-3 h-3" /> Pendiente</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Ventana de vista previa para el equipo (calendario): carga los archivos actuales al abrirse
export function TeamInstagramPreviewDialog({ open, onOpenChange, post, clientId, clientName, Dialog, DialogContent, DialogHeader, DialogTitle, useMedia, brandKitAPI }) {
  const media = useMedia(open ? post : null);
  const [profile, setProfile] = useState({ nombre: clientName });
  useEffect(() => {
    if (!open || !clientId) return;
    brandKitAPI.get(clientId).then((r) => {
      const kit = r?.data || {};
      let logo = null;
      for (const lg of kit.logos || []) {
        const f = (lg.files || []).find((x) => (x.content_type || '').startsWith('image/'));
        if (f) { logo = f.id; break; }
      }
      setProfile({ nombre: clientName, instagram_user: kit.instagram_user || '', logo_file_id: logo });
    }).catch(() => {});
  }, [open, clientId, clientName, brandKitAPI]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="team-ig-preview-dialog">
        <DialogHeader><DialogTitle className="text-base">Vista previa en Instagram</DialogTitle></DialogHeader>
        {!profile.instagram_user && <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1" data-testid="team-ig-no-user">Este cliente no tiene usuario de Instagram en su Kit de Marca; se muestra uno aproximado.</p>}
        {media === null ? <div className="py-16 text-center text-sm text-muted-foreground">Cargando…</div> : <InstagramPreview post={post} media={media} profile={profile} />}
      </DialogContent>
    </Dialog>
  );
}
